import { Copy, Link2, LoaderCircle, Share2, X } from "lucide-react";
import { t } from "../i18n";
import type { ShareInfo } from "../pages/chat/ChatPage";

export function ShareDialog({
  info,
  url,
  busy,
  onEnable,
  onDisable,
  onClose,
}: {
  info: ShareInfo | null;
  url: string;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        className="w-full max-w-lg rounded-2xl border border-stone-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-stone-900"
      >
        <div className="flex items-start gap-3">
          <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-lumen-50 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
            <Share2 size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="share-title" className="font-bold">
              {t("Share chat")}
            </h2>
            <p className="mt-1 text-sm leading-6 text-stone-500">
              {t(
                "Anyone with the link can read this chat. They cannot reply or make changes.",
              )}
            </p>
          </div>
          <button
            type="button"
            className="button p-1.5"
            aria-label={t("Close")}
            onClick={onClose}
            disabled={busy}
          >
            <X size={16} />
          </button>
        </div>
        {!info ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-stone-400">
            <LoaderCircle className="animate-spin text-lumen-600" size={17} />{" "}
            {t("Loading…")}
          </div>
        ) : info.enabled ? (
          <>
            {url ? (
              <div className="mt-5 flex items-center gap-2 rounded-xl border border-stone-200 bg-stone-50 p-2 dark:border-white/10 dark:bg-white/5">
                <Link2 className="ml-1 shrink-0 text-stone-400" size={16} />
                <input
                  readOnly
                  aria-label={t("Sharing link")}
                  value={url}
                  className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                />
                <button
                  type="button"
                  className="button shrink-0 px-3 py-1.5 text-xs"
                  onClick={() => void navigator.clipboard.writeText(url)}
                >
                  <Copy size={14} /> {t("Copy")}
                </button>
              </div>
            ) : (
              <div className="mt-5 rounded-xl bg-stone-50 p-4 dark:bg-white/5">
                <p className="text-sm text-stone-500">
                  {t(
                    "This link is unavailable in this browser session. Creating a new link will stop the previous link from working.",
                  )}
                </p>
                <button
                  type="button"
                  className="button-primary mt-3"
                  disabled={busy}
                  onClick={onEnable}
                >
                  {busy && <LoaderCircle className="animate-spin" size={15} />}
                  {t("Create new link")}
                </button>
              </div>
            )}
            <div className="mt-5 flex items-center justify-between gap-3">
              <span className="text-xs text-stone-400">
                {t("Disabling makes this URL stop working immediately.")}
              </span>
              <button
                type="button"
                className="button shrink-0 text-red-600"
                disabled={busy}
                onClick={onDisable}
              >
                {busy && <LoaderCircle className="animate-spin" size={14} />}{" "}
                {t("Disable link")}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-5 flex items-center justify-between gap-4 rounded-xl bg-stone-50 p-4 dark:bg-white/5">
            <p className="text-sm text-stone-500">
              {t("Sharing is currently disabled.")}
            </p>
            <button
              type="button"
              className="button-primary shrink-0"
              disabled={busy}
              onClick={onEnable}
            >
              {busy && <LoaderCircle className="animate-spin" size={15} />}{" "}
              {t("Create link")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
